import React, { useCallback, useRef, useState } from 'react'
import { buildCapabilityChips } from './agentCapabilities'
import { formatAgentModel } from './formatAgentModel'
import {
  AGENT_ICON_SRC,
  agentColors,
  glassCardOuterStyle,
  glassCardInnerStyle,
} from './agentChatLayout'

const CARD_STYLES = `
@keyframes agentShimmer {
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
}
@keyframes agentPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
.agent-glass-btn {
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease;
  cursor: pointer;
  border: 1px solid rgba(255,255,255,0.45);
}
.agent-glass-btn:hover:not(:disabled) {
  transform: translateY(-2px) scale(1.03);
  box-shadow: 0 6px 20px rgba(193, 127, 89, 0.25);
}
.agent-glass-btn:active:not(:disabled) {
  transform: translateY(0) scale(0.97);
  box-shadow: inset 0 2px 6px rgba(28, 25, 23, 0.12);
}
.agent-glass-btn-primary {
  background: linear-gradient(135deg, #c17f59 0%, #a86b48 100%);
  color: #fff;
  border-color: rgba(193, 127, 89, 0.5);
}
.agent-glass-btn-primary:hover:not(:disabled) {
  box-shadow: 0 8px 24px rgba(193, 127, 89, 0.45);
}
.agent-glass-btn-secondary {
  background: rgba(255, 255, 255, 0.35);
  color: ${agentColors.textPrimary};
  backdrop-filter: blur(8px);
}
.agent-glass-shimmer-border {
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
  background-size: 200% 100%;
  animation: agentShimmer 4s linear infinite;
}
`

function SkeletonBlock({ width, height = 12, style = {} }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: 6,
        background: 'rgba(255,255,255,0.35)',
        animation: 'agentPulse 1.4s ease-in-out infinite',
        ...style,
      }}
    />
  )
}

/**
 * Centered square 3D glass card — logo, name, capabilities, model, CTAs.
 */
export default function AgentHeroGlassCard({
  manifest,
  permLoading,
  permError,
  onMyAccess,
  onLetsChat,
}) {
  const outerRef = useRef(null)
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 })
  const [shine, setShine] = useState({ x: 50, y: 50 })

  const onMouseMove = useCallback((e) => {
    const el = outerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    const isMobile = window.innerWidth <= 640
    const max = isMobile ? 4 : 8
    setTilt({
      ry: (x - 0.5) * max * 2,
      rx: (0.5 - y) * max * 2,
    })
    setShine({ x: x * 100, y: y * 100 })
  }, [])

  const onMouseLeave = useCallback(() => {
    setTilt({ rx: 0, ry: 0 })
    setShine({ x: 50, y: 50 })
  }, [])

  const chips = buildCapabilityChips(manifest)
  const modelInfo = formatAgentModel(manifest?.llm)
  const visibleChips = chips.slice(0, 4)
  const extraCount = chips.length - visibleChips.length

  const shadowX = tilt.ry * 0.8
  const shadowY = -tilt.rx * 0.8 + 8

  return (
    <>
      <style>{CARD_STYLES}</style>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: 24,
          padding: '0 12px',
        }}
      >
        <div
          ref={outerRef}
          style={glassCardOuterStyle}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
        >
          <div
            style={{
              ...glassCardInnerStyle,
              transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) translateZ(0)`,
              boxShadow: `${shadowX}px ${shadowY}px 32px rgba(28, 25, 23, 0.14), 0 0 0 1px rgba(255,255,255,0.5) inset`,
            }}
          >
            <div
              className="agent-glass-shimmer-border"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                opacity: 0.7,
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(circle at ${shine.x}% ${shine.y}%, rgba(255,255,255,0.35) 0%, transparent 55%)`,
                pointerEvents: 'none',
                borderRadius: 20,
              }}
            />

            <div
              style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%',
                justifyContent: 'space-between',
                padding: '20px 18px 18px',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    padding: 3,
                    background: 'linear-gradient(135deg, #e8ddd4, #c17f59)',
                    boxShadow: '0 4px 16px rgba(193, 127, 89, 0.3)',
                    marginBottom: 12,
                  }}
                >
                  <img
                    src={AGENT_ICON_SRC}
                    alt=""
                    width={66}
                    height={66}
                    style={{
                      display: 'block',
                      borderRadius: '50%',
                      background: agentColors.bgWarm,
                    }}
                  />
                </div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: 17,
                    fontWeight: 700,
                    color: agentColors.textPrimary,
                    letterSpacing: '-0.02em',
                    textAlign: 'center',
                  }}
                >
                  Knowledge Assistant
                </h2>
              </div>

              <div style={{ width: '100%', textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: agentColors.textMuted,
                    marginBottom: 8,
                  }}
                >
                  Capabilities
                </div>
                {permLoading ? (
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <SkeletonBlock width={56} height={24} />
                    <SkeletonBlock width={72} height={24} />
                    <SkeletonBlock width={48} height={24} />
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {visibleChips.length === 0 ? (
                      <span style={{ fontSize: 12, color: agentColors.textMuted }}>Org-wide only</span>
                    ) : (
                      visibleChips.map((c) => (
                        <span
                          key={c.id}
                          style={{
                            padding: '4px 10px',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 500,
                            background: 'rgba(255,255,255,0.45)',
                            border: `1px solid ${agentColors.borderSoft}`,
                            color: agentColors.textSecondary,
                          }}
                        >
                          {c.label}
                        </span>
                      ))
                    )}
                    {extraCount > 0 ? (
                      <span style={{ fontSize: 11, color: agentColors.textMuted }}>+{extraCount}</span>
                    ) : null}
                  </div>
                )}

                <div style={{ marginTop: 14 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      color: agentColors.textMuted,
                      marginBottom: 4,
                    }}
                  >
                    Model
                  </div>
                  {permLoading ? (
                    <SkeletonBlock width={120} height={14} style={{ margin: '0 auto' }} />
                  ) : (
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: modelInfo.color,
                        fontFamily: 'ui-monospace, monospace',
                      }}
                    >
                      {modelInfo.label}
                    </div>
                  )}
                </div>

                {permError ? (
                  <p style={{ margin: '10px 0 0', fontSize: 12, color: agentColors.danger }}>{permError}</p>
                ) : null}
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  width: '100%',
                  marginTop: 8,
                }}
              >
                <button
                  type="button"
                  className="agent-glass-btn agent-glass-btn-secondary"
                  disabled={permLoading}
                  onClick={onMyAccess}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  My Access
                </button>
                <button
                  type="button"
                  className="agent-glass-btn agent-glass-btn-primary"
                  disabled={permLoading}
                  onClick={onLetsChat}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Let&apos;s chat →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
